import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import supabase from '../../supabase';
import Userdiv from '../../components/userdiv';
import CustomerReview from '../../components/customerReviews';
import FullPageLoader from '../../components/FullPageLoader';
import { Helmet } from 'react-helmet-async';

const REVIEWS_PER_PAGE = 10;

export default function Userpage() {
  const { id } = useParams();
  
  // --- State ---
  const [targetUser, setTargetUser] = useState(null);
  const [userReviews, setUserReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [totalHelpfulVotes, setTotalHelpfulVotes] = useState(0);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const fetchFullProfile = async () => {
      setLoading(true);
      setError(false);

      try {
        const from = (currentPage - 1) * REVIEWS_PER_PAGE;
        const to = from + REVIEWS_PER_PAGE - 1;

        // 1. Fetch User Data & Paginated Reviews
        const [userRes, reviewsRes] = await Promise.all([
          supabase.from('users').select('*').eq('id', id).single(),
          supabase.from('reviews')
            .select('*', { count: 'exact' })
            .eq('user_id', id)
            .order('date', { ascending: false })
            .range(from, to)
        ]);

        if (userRes.error || !userRes.data) {
          setError(true);
          setLoading(false);
          return;
        }

        setTargetUser(userRes.data);
        setUserReviews(reviewsRes.data || []);
        setTotalCount(reviewsRes.count || 0);

        // 2. Fetch Total Helpful Votes (Likes)
        // We look at the 'likes' table and count entries where the review belongs to this user
        const { data: likesData, error: likesError } = await supabase
          .from('reviews')
          .select(`
            id,
            likes:likes(count)
          `)
          .eq('user_id', id);

        if (!likesError && likesData) {
          const total = likesData.reduce((acc, review) => {
            return acc + (review.likes?.[0]?.count || 0);
          }, 0);
          setTotalHelpfulVotes(total);
        }

      } catch (err) {
        console.error('Critical Fetch Error:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchFullProfile();
  }, [id, currentPage]);

  const totalPages = Math.ceil(totalCount / REVIEWS_PER_PAGE);

  if (loading) return <FullPageLoader />;

  if (error || !targetUser) {
    return (
      <div className="w-full h-screen bg-[#f3f3f3] flex flex-col items-center justify-center p-6 text-center">
        <h1 className="font-staat text-8xl text-red-500 mb-4">404</h1>
        <h2 className="font-staat text-4xl text-[#323232] uppercase tracking-tighter">User Not Found</h2>
        <p className="font-poppins text-gray-500 mt-4 max-w-md">
          The profile you are looking for doesn't exist or has been moved. 
        </p>
        <a href="/" className="mt-8 px-8 py-3 bg-[#42CDB4] text-white font-staat text-xl rounded-lg hover:bg-[#1E7D6B] transition-colors">
          Return Home
        </a>
      </div>
    );
  }
  return (
    <div className="w-full min-h-screen bg-[#f8f9fa] pb-[100px] flex items-center flex-col">

      <Helmet>
        <title>{targetUser.fullname ? `${targetUser.fullname}'s Profile` : "User Profile"}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Userdiv 
        reviewCount={totalCount} 
        targetUser={targetUser} 
        helpfulVotes={totalHelpfulVotes} 
      />
      
      <div className="w-full max-w-[1224px] px-4 sm:px-10">
        <h2 className="text-[32px] sm:text-[42px] font-staat text-[#323232] mt-[80px] mb-[40px] text-center lg:text-left">
          {targetUser.fullname.split(' ')[0]}'s Activity
        </h2>

        {userReviews.length > 0 ? (
          <div className="flex flex-col gap-[25px] w-full items-center">
            <div className="flex flex-col gap-[25px] w-full">
              {userReviews.map((review) => (
                <CustomerReview
                  key={review.id}
                  id={review.id}
                  userid={review.user_id}
                  stars={review.stars}
                  text={review.text}
                  date={review.date}
                  author={review.author}
                  title={review.title}
                  img={targetUser.img}
                  isVerified={review.isVerified}
                  countr={review.countr}
                  otherrevs={totalCount}
                  showReply={false}
                  isActivity={true}
                  website={review.company}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-6 mt-16 mb-10 w-full">
                <button 
                  disabled={currentPage === 1}
                  onClick={() => {
                    setCurrentPage(prev => prev - 1);
                    window.scrollTo({ top: 400, behavior: 'smooth' });
                  }}
                  className="px-8 py-2 bg-white border border-gray-200 rounded-full font-staat text-xl text-[#323232] hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  PREVIOUS
                </button>
                
                <span className="font-poppins text-gray-500 font-semibold tracking-wide">
                  {currentPage} / {totalPages}
                </span>

                <button 
                  disabled={currentPage === totalPages}
                  onClick={() => {
                    setCurrentPage(prev => prev + 1);
                    window.scrollTo({ top: 400, behavior: 'smooth' });
                  }}
                  className="px-8 py-2 bg-[#42CDB4] border border-[#42CDB4] rounded-full font-staat text-xl text-white hover:bg-[#1E7D6B] disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  NEXT
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[30px] border border-dashed border-gray-300 transform rotate-1 shadow-sm">
            <div className="transform -rotate-1 flex flex-col items-center">
              <div className="w-20 h-20 bg-[#42CDB4]/10 rounded-full flex items-center justify-center mb-6">
                <span className="text-4xl text-[#42CDB4]">?</span>
              </div>
              <h3 className="font-staat text-3xl text-gray-400 uppercase tracking-widest">
                No Reviews Yet
              </h3>
              <p className="font-poppins text-gray-400 mt-2 text-center px-6">
                {targetUser.fullname} hasn't shared any experiences yet.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}